// helperFunctions.js
import mapConfig from "./mapConfig";

const networkLayerConfig = mapConfig.layers.network;

// Adds or updates the Mapbox source + layer for the network
export function addOrUpdateNetwork(map, networkFc) {
  if (!map || !networkFc || !map.isStyleLoaded()) return;
  
  const sourceId = networkLayerConfig.sourceId;
  const layerId = networkLayerConfig.layerId;

  const src = map.getSource(sourceId);

  if (!src) {
    map.addSource(sourceId, {
      type: "geojson",
      data: networkFc,
    });

    map.addLayer({
      id: layerId,
      type: networkLayerConfig.type || "line",
      source: sourceId,
      paint: networkLayerConfig.paint,
      layout: networkLayerConfig.layout,
    });
  } else {
    src.setData(networkFc);
  }
}

// Toggle network layer visibility
export function setNetworkVisibility(map, visible) {
  if (!map) return;
  const layerId = networkLayerConfig.layerId;

  if (!map.getLayer(layerId)) return;

  map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
}

// Fits the map view to the FeatureCollection of the network
export function fitToNetwork(map, networkFc) {
  if (!map || !networkFc?.features?.length) return;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const feat of networkFc.features) {
    const coordsArrays = flattenCoords(feat.geometry);
    for (const [x, y] of coordsArrays) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  if (minX === Infinity) return;

  map.fitBounds(
    [
      [minX, minY],
      [maxX, maxY],
    ],
    {
      padding:
        typeof networkLayerConfig.fitBoundsPadding === "number"
          ? networkLayerConfig.fitBoundsPadding
          : 40,
      duration: 0,
    }
  );
}

// Normalizes coordinates for LineString / MultiLineString / Point
export function flattenCoords(geometry) {
  if (!geometry?.coordinates) return [];

  const { type, coordinates } = geometry;

  switch (type) {
    case "LineString":
      return coordinates;

    case "MultiLineString":
      return coordinates.flat();

    case "Point":
      return [coordinates];

    default:
      return [];
  }
}

// Collect unique wayIds (PathIDs) for a given geometry over the network layer
export function collectPathIdsFromGeometry(
  map,
  geometry,
  layerId = networkLayerConfig.layerId
) {
  if (!map || !geometry) return [];

  const type = geometry.type;
  const pathIdSet = new Set();

  if (type === "Point") {
    const [lng, lat] = geometry.coordinates;
    const point = map.project([lng, lat]);

    const features = map.queryRenderedFeatures(point, {
      layers: [layerId],
    });

    features.forEach((f) => {
      const wayId = f.properties && f.properties.wayId;
      if (wayId) pathIdSet.add(wayId);
    });
  } else if (type === "LineString") {
    const coords = geometry.coordinates || [];

    coords.forEach((coord) => {
      const point = map.project(coord);
      const features = map.queryRenderedFeatures(point, {
        layers: [layerId],
      });

      features.forEach((f) => {
        const wayId = f.properties && f.properties.wayId;
        if (wayId) pathIdSet.add(wayId);
      });
    });
  }

  return [...pathIdSet];
}
